import React, { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { ExtraCost, ChangedItem, OptionalId, ProjectFeeSet, LocalUnsaved, ResidentialUnit } from '@/types';
import { Modal, ModalHeader, ModalBody, ModalFooter, LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { Button } from '@/components/Buttons';
import { getExtraCosts, saveExtraCost } from '@/services/extra-costs';
import ExtraCostRow from './ExtraCostRow';
import { getProjectFeeSets } from '@/services/fees';
import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { ValidationScope } from '@/components/forms/validation';
import { useValidationManager } from '@/utils/hooks';
import { getUniqueId } from '@/utils/html';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';

interface ExtraCostsDialogProps {
    onClose(): void;
    onAccept?(): void;
    show: boolean;
    residentialUnit: ResidentialUnit;
}

const ExtraCostsDialog: React.FC<ExtraCostsDialogProps> = ({ show, onClose, onAccept, residentialUnit }) => {
    const projectId = useSelector((state: IState) => state.global.activeProject!.id);

    const [extraCosts, setExtraCosts] = useState<LocalUnsaved<ExtraCost>[] | null>(null);
    const [projectFeeSets, setProjectFeeSets] = useState<ProjectFeeSet[] | null>(null);

    const [saving, setSaving] = useState(false);

    const validationManager = useValidationManager();

    const getEmptyExtraCost = () => {
        return {
            residentialUnitId: residentialUnit.id,
            amount: 0,
            title: '',
            description: '',
            projectFeeSetId: null,
            _internalId: getUniqueId(),
        };
    };

    const handleOpen = async () => {
        setSaving(false);

        setExtraCosts(null);

        if (!projectFeeSets) {
            setProjectFeeSets(await getProjectFeeSets(projectId));
        }
        const extraCosts = await getExtraCosts(residentialUnit.id);
        if (extraCosts.length === 0) {
            setExtraCosts([getEmptyExtraCost()]);
        } else {
            setExtraCosts(extraCosts);
        }
    };

    const handleChange = (item: ChangedItem<OptionalId<ExtraCost>>) => {
        setExtraCosts(item.updateArray(extraCosts!));
    };

    const handleCancelClick = () => {
        triggerHandler(onClose);
    };

    const handleDelete = (item: OptionalId<ExtraCost>) => {
        setExtraCosts(extraCosts!.filter((x) => x !== item));
    };

    const handleAdd = () => {
        setExtraCosts(extraCosts!.concat(getEmptyExtraCost()));
    };

    const handleAccept = async (e) => {
        if (validationManager.validate().length > 0) {
            return;
        }

        setSaving(true);

        try {
            await saveExtraCost(residentialUnit.id, extraCosts!);
            triggerHandler(onClose);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen={show} toggle={handleCancelClick} onOpen={handleOpen} size="lg">
            <ModalHeader>
                <Trans>Add extra costs</Trans>
            </ModalHeader>
            <ModalBody>
                {extraCosts && projectFeeSets ? (
                    <ValidationScope validationManager={validationManager}>
                        {extraCosts.map((ec) => (
                            <ExtraCostRow
                                key={ec._internalId || ec.id}
                                value={ec}
                                onChange={handleChange}
                                onDelete={handleDelete}
                                projectFeeSets={projectFeeSets}
                            />
                        ))}

                        <Button color="link" onClick={handleAdd} icon={faPlus}>
                            <Trans>Add</Trans>
                        </Button>
                    </ValidationScope>
                ) : (
                    <LoadingIndicator />
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="tertiary" onClick={handleCancelClick} disabled={saving}>
                    <Trans>Cancel</Trans>
                </Button>
                <Button color="primary" onClick={handleAccept} loading={saving}>
                    <Trans>Save</Trans>
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default ExtraCostsDialog;
